import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Plus, Trash2, RefreshCw, X, Sun, Moon,
  Crown, Gift, Repeat, Calendar, Bell, Sparkles, GitBranch,
} from 'lucide-react';
import { useTheme, useFeatureFlags } from '../components/Navbar';
import useAutoLogout from '../hooks/useAutoLogout';
import Toast from '../components/Toast';
import API_BASE from '../config';

/* ---- shared theme tokens (same system as Campaigns/Dashboard) ---- */
const TK = {
  light: {
    pageBg:'#F5F6FA', card:'#FFFFFF', border:'#ECEEF2',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', rowBdr:'#F0F2F5', input:'#FFFFFF',
    btnPri:'#5B4EE8', btnPriH:'#4C3FD4', btnPriTxt:'#fff',
    btnSec:'#fff', btnSecTxt:'#374151', btnSecBdr:'#ECEEF2',
    chip:'#F3F4F6', chipTxt:'#374151', overlay:'rgba(15,22,35,0.32)',
    toggleBg:'#ECEEF2', toggleActive:'#fff', good:'#16A34A', track:'#E5E7EB',
  },
  dark: {
    pageBg:'#0D0D14', card:'#131320', border:'#1E1E2E',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    row:'#18182A', rowBdr:'#1E1E2E', input:'#18182A',
    btnPri:'#6C63FF', btnPriH:'#5B53E8', btnPriTxt:'#fff',
    btnSec:'#1A1A2A', btnSecTxt:'#D1D5DB', btnSecBdr:'#1E1E2E',
    chip:'#1A1A2A', chipTxt:'#C7CBDA', overlay:'rgba(0,0,0,0.55)',
    toggleBg:'#1A1A2A', toggleActive:'#1E1E2E', good:'#34D399', track:'#2A2A3A',
  },
};

const CAT_COLOR = { Marketing:'#EC4899', Transactional:'#3B82F6', General:'#6B7280' };

/* preset templates customers can pick from */
const TRIGGERS = [
  { value:'vip_invitation', label:'VIP Invitation',       category:'Marketing',     channel:'whatsapp', template:'vip_invitation',      Icon:Crown,    hint:'Invite high-value customers to an exclusive event or offer.', offsetLabel:'' },
  { value:'birthday',       label:'Birthday Offer',       category:'Marketing',     channel:'whatsapp', template:'birthday_offer',      Icon:Gift,     hint:'Greet customers with a discount on their birthday.', offsetLabel:'Days before/after birthday' },
  { value:'win_back',       label:'Win-back / Inactive',  category:'Marketing',     channel:'email',    template:'win_back_campaign',   Icon:Repeat,   hint:'Re-engage customers inactive for N days.', offsetLabel:'Days of inactivity' },
  { value:'appointment',    label:'Appointment Reminder', category:'Transactional', channel:'whatsapp', template:'appointment_reminder',Icon:Calendar, hint:'Remind customers before an upcoming appointment.', offsetLabel:'Days before appointment (use negative)' },
  { value:'confirmation',   label:'Confirmation',         category:'Transactional', channel:'sms',      template:'booking_confirmation',Icon:Bell,     hint:'Confirm a booking or order right after it happens.', offsetLabel:'' },
  { value:'custom_event',   label:'Custom Event',         category:'General',       channel:'whatsapp', template:'',                    Icon:Sparkles, hint:'Fire on any custom event you push to the CDP.', offsetLabel:'Days offset' },
];
const trigMeta = (v) => TRIGGERS.find(t => t.value === v) || { label:v||'—', category:'General', Icon:Sparkles, offsetLabel:'Days offset' };
const categoryOf = (v) => trigMeta(v).category;

const CHANNELS = [
  { label:'WhatsApp', value:'whatsapp', flag:'whatsapp_campaigns' },
  { label:'Email',    value:'email', flag:'email_journeys' },
  { label:'SMS',      value:'sms', flag:'sms_channel' },
];

const getToken = () =>
  localStorage.getItem('martech_token') || localStorage.getItem('token') ||
  sessionStorage.getItem('martech_token') || sessionStorage.getItem('token') || '';

function toIST(d) {
  if (!d) return '—';
  const s = String(d);
  const dt = new Date(s.endsWith('Z') ? s : s.replace(' ', 'T') + 'Z');
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('en-IN', {
    timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:true,
  });
}

function channelPill(ch) {
  const c = (ch || '').toLowerCase();
  if (c === 'whatsapp') return { bg:'rgba(37,211,102,0.14)', color:'#16a34a' };
  if (c === 'email')    return { bg:'rgba(59,130,246,0.14)', color:'#2563eb' };
  if (c === 'sms')      return { bg:'rgba(245,158,11,0.16)', color:'#b45309' };
  return { bg:'rgba(107,114,128,0.16)', color:'#6b7280' };
}

function ThemeToggle({ tk }) {
  const { theme, setTheme } = useTheme();
  const btn = (t, Icon, label) => (
    <button type="button" key={t} onClick={()=>setTheme(t)} style={{display:'flex',alignItems:'center',gap:5,background:theme===t?tk.toggleActive:'transparent',border:'none',borderRadius:6,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:500,color:theme===t?tk.text:tk.sub,boxShadow:theme===t?'0 1px 3px rgba(0,0,0,0.08)':'none',transition:'all 0.15s',whiteSpace:'nowrap'}}>
      <Icon size={13}/>{label}
    </button>
  );
  return <div style={{display:'flex',background:tk.toggleBg,borderRadius:8,padding:3}}>{btn('light',Sun,'Light')}{btn('dark',Moon,'Dark')}</div>;
}

function Stat({ Icon, label, value, accent, loading, tk }) {
  return (
    <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
      <div style={{width:34,height:34,borderRadius:9,background:accent+'18',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Icon size={17} color={accent} strokeWidth={2}/>
      </div>
      <div>
        <div style={{fontSize:28,fontWeight:700,color:tk.text,letterSpacing:'-0.5px',lineHeight:1}}>{loading?'—':value}</div>
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.05em',marginTop:6}}>{label}</div>
      </div>
    </div>
  );
}

function Toggle({ active, onChange, tk, disabled }) {
  return (
    <button type="button" onClick={onChange} disabled={disabled}
      style={{position:'relative',width:38,height:22,borderRadius:999,border:'none',cursor:disabled?'not-allowed':'pointer',background:active?tk.good:tk.track,transition:'background 0.2s',flexShrink:0,opacity:disabled?0.6:1}}>
      <span style={{position:'absolute',top:3,left:active?19:3,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 2px rgba(0,0,0,0.25)'}}/>
    </button>
  );
}

const EMPTY_FORM = { name:'', trigger_type:'vip_invitation', channel:'whatsapp', template:'vip_invitation', offset_days:0 };

export default function Automation() {
  useAutoLogout();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { flags } = useFeatureFlags();
  const visibleChannels = CHANNELS.filter(c => flags[c.flag] !== false);
  const tk = TK[theme];

  const [rules, setRules]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast]         = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId]       = useState(null);
  const [typeF, setTypeF]         = useState('All');

  const showToast = (msg, type='success') => setToast({ msg, type });

  const fetchRules = useCallback(async (isRefresh=false) => {
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/automations`, { headers:{ Authorization:`Bearer ${token}` } });
      if (res.status === 401) { navigate('/login'); return; }
      const data = await res.json();
      setRules(Array.isArray(data) ? data : (data.automations || []));
    } catch {
      showToast('Failed to load automation rules', 'error');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => { fetchRules(false); }, [fetchRules]);

  const pickPreset = (t) => {
    const m = trigMeta(t);
    setForm(f => ({ ...f, trigger_type:t, channel:m.channel || f.channel, template:m.template || '' }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!form.name.trim()) { showToast('Rule name is required', 'error'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/automations`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          trigger_type: form.trigger_type,
          channel: form.channel,
          conditions: { template: form.template.trim(), offset_days: Number(form.offset_days) || 0 },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed');
      if (data.automation) setRules(prev => [data.automation, ...prev]); else fetchRules(true);
      showToast('Automation rule created');
      setShowModal(false); setForm(EMPTY_FORM);
    } catch (err) {
      showToast(err.message || 'Failed to create rule', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    if (busyId) return;
    const token = getToken();
    setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/api/v1/automations/${id}/toggle`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error();
      setRules(prev => prev.map(r => r.id === id ? { ...r, status: data.automation?.status ?? (r.status==='active'?'inactive':'active') } : r));
    } catch {
      showToast('Failed to toggle rule', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (busyId) return;
    const token = getToken();
    setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/api/v1/automations/${id}`, {
        method:'DELETE', headers:{ Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error();
      setRules(prev => prev.filter(r => r.id !== id));
      showToast('Rule deleted');
    } catch {
      showToast('Failed to delete rule', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const enriched = useMemo(() => rules.map(r => ({ ...r, category: categoryOf(r.trigger_type) })), [rules]);
  const stats = useMemo(() => ({
    total: enriched.length,
    active: enriched.filter(r => r.status === 'active').length,
    paused: enriched.filter(r => r.status !== 'active').length,
  }), [enriched]);
  const typeCounts = useMemo(() => ({
    All: enriched.length,
    Marketing: enriched.filter(r => r.category === 'Marketing').length,
    Transactional: enriched.filter(r => r.category === 'Transactional').length,
  }), [enriched]);
  const visible = useMemo(() => typeF === 'All' ? enriched : enriched.filter(r => r.category === typeF), [enriched, typeF]);

  const selStyle = { background:tk.input, color:tk.text, border:`1px solid ${tk.border}`, borderRadius:10, padding:'10px 12px', fontSize:13, fontWeight:500, cursor:'pointer', outline:'none', width:'100%', boxSizing:'border-box' };
  const inpStyle = { ...selStyle, cursor:'text' };
  const th = { textAlign:'left', padding:'8px 14px', fontSize:10, fontWeight:700, color:tk.muted, textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:`1px solid ${tk.border}`, whiteSpace:'nowrap' };
  const td = { padding:'12px 14px', borderBottom:`1px solid ${tk.rowBdr}`, fontSize:12.5, color:tk.text, verticalAlign:'middle' };
  const curPreset = trigMeta(form.trigger_type);

  return (
    <>
      <style>{`
        .ato-wrap{padding:20px 24px;box-sizing:border-box;min-height:100vh}
        .ato-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px}
        .ato-hdr{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:16px}
        .ato-hdr-r{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .ato-chips{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:14px}
        .ato-desktop{display:block}
        .ato-mobile{display:none}
        .ato-tbl{width:100%;border-collapse:collapse}
        .ato-presets{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
        @media(max-width:640px){
          .ato-wrap{padding:72px 16px 18px}
          .ato-stats{grid-template-columns:1fr;gap:8px}
          .ato-hdr{flex-direction:column;gap:10px}
          .ato-hdr-r{width:100%;justify-content:space-between}
          .ato-desktop{display:none}
          .ato-mobile{display:flex;flex-direction:column;gap:10px}
          .ato-presets{grid-template-columns:1fr 1fr}
        }
      `}</style>

      {toast && <Toast message={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}

      <div className="ato-wrap" style={{background:tk.pageBg}}>

        {/* Header */}
        <div className="ato-hdr">
          <div>
            <h1 style={{margin:0,fontSize:22,fontWeight:700,color:tk.text,letterSpacing:'-0.3px'}}>Automation</h1>
            <p style={{margin:'4px 0 0',fontSize:13,color:tk.sub}}>Trigger-based rules that fire a single message when an event happens.</p>
          </div>
          <div className="ato-hdr-r">
            <ThemeToggle tk={tk}/>
            <button type="button" onClick={()=>fetchRules(true)} disabled={refreshing}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:500,cursor:'pointer'}}>
              <RefreshCw size={14} className={refreshing?'animate-spin':''}/> Refresh
            </button>
            <button type="button" onClick={()=>{ setForm(EMPTY_FORM); setShowModal(true); }}
              onMouseEnter={e=>e.currentTarget.style.background=tk.btnPriH}
              onMouseLeave={e=>e.currentTarget.style.background=tk.btnPri}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnPri,color:tk.btnPriTxt,border:'none',borderRadius:8,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer',transition:'background 0.15s'}}>
              <Plus size={14}/> Create Rule
            </button>
          </div>
        </div>

        {/* Journeys cross-link */}
        <div style={{display:'flex',alignItems:'center',gap:10,background:tk.accentBg,border:`1px solid ${tk.accent}33`,borderRadius:10,padding:'10px 14px',marginBottom:16}}>
          <GitBranch size={15} color={tk.accent}/>
          <span style={{fontSize:12.5,color:tk.sub}}>
            Need a multi-step sequence (welcome series, drip follow-ups)? Use <button type="button" onClick={()=>navigate('/journeys')} style={{background:'none',border:'none',padding:0,color:tk.accentTxt,fontWeight:700,cursor:'pointer',fontSize:12.5}}>Journeys</button>. Automation rules send one message per trigger.
          </span>
        </div>

        {/* Stats */}
        <div className="ato-stats">
          <Stat Icon={Zap}   label="Total Rules" value={stats.total}  accent="#6C63FF" loading={loading} tk={tk}/>
          <Stat Icon={Bell}  label="Active"      value={stats.active} accent="#10B981" loading={loading} tk={tk}/>
          <Stat Icon={Repeat}label="Paused"      value={stats.paused} accent="#F59E0B" loading={loading} tk={tk}/>
        </div>

        {/* Type filter */}
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,letterSpacing:'0.08em',textTransform:'uppercase',margin:'4px 0 8px'}}>Type</div>
        <div className="ato-chips">
          {['All','Marketing','Transactional'].map(c => {
            const active = typeF===c;
            const dot = c==='All'?tk.accent:CAT_COLOR[c];
            return (
              <button key={c} type="button" onClick={()=>setTypeF(c)}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
                  background:active?tk.accentBg:tk.card, color:active?tk.accentTxt:tk.sub, border:`1px solid ${active?tk.accent+'40':tk.border}`}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:dot}}/>{c}
                <span style={{color:tk.muted,fontWeight:700}}>{typeCounts[c]||0}</span>
              </button>
            );
          })}
        </div>

        {/* Results */}
        {loading ? (
          <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'40px 0',textAlign:'center',color:tk.muted,fontSize:13}}>Loading…</div>
        ) : visible.length===0 ? (
          <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'48px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
            <Zap size={28} color={tk.muted} style={{marginBottom:10}}/>
            <div style={{fontSize:13,fontWeight:600,color:tk.text,marginBottom:4}}>No automation rules yet</div>
            <div style={{fontSize:12,color:tk.muted}}>Create your first rule — try a VIP Invitation or Appointment Reminder.</div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="ato-desktop" style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,overflowX:'auto'}}>
              <table className="ato-tbl">
                <thead><tr>
                  <th style={th}>Rule</th><th style={th}>Type</th><th style={th}>Trigger</th>
                  <th style={th}>Channel</th><th style={th}>Status</th><th style={th}>Created</th><th style={th}></th>
                </tr></thead>
                <tbody>
                  {visible.map((r,i) => {
                    const cp = channelPill(r.channel); const m = trigMeta(r.trigger_type);
                    return (
                      <tr key={r.id} style={{background:i%2?tk.row:'transparent'}}>
                        <td style={{...td,fontWeight:600}}>
                          {r.name}
                          {r.conditions?.template ? <div style={{fontSize:11,color:tk.muted,fontWeight:400,marginTop:2}}>{r.conditions.template}</div> : null}
                        </td>
                        <td style={td}>
                          <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,color:tk.sub}}>
                            <span style={{width:7,height:7,borderRadius:'50%',background:CAT_COLOR[r.category]}}/>{r.category}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{display:'inline-flex',alignItems:'center',gap:5,color:tk.text}}><m.Icon size={13} color={tk.sub}/>{m.label}</span>
                        </td>
                        <td style={td}><span style={{background:cp.bg,color:cp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize'}}>{r.channel}</span></td>
                        <td style={td}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <Toggle active={r.status==='active'} onChange={()=>handleToggle(r.id)} tk={tk} disabled={busyId===r.id}/>
                            <span style={{fontSize:11,fontWeight:600,color:r.status==='active'?tk.good:tk.muted}}>{r.status==='active'?'Active':'Paused'}</span>
                          </div>
                        </td>
                        <td style={{...td,color:tk.sub,whiteSpace:'nowrap',fontSize:11.5}}>{toIST(r.created_at)}</td>
                        <td style={td}>
                          <button type="button" onClick={()=>handleDelete(r.id)} disabled={busyId===r.id} title="Delete"
                            style={{background:'transparent',border:'none',cursor:'pointer',color:tk.muted,padding:4,borderRadius:6,opacity:busyId===r.id?0.4:1}}>
                            <Trash2 size={15}/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="ato-mobile">
              {visible.map(r => {
                const cp = channelPill(r.channel); const m = trigMeta(r.trigger_type);
                return (
                  <div key={r.id} style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'14px 15px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:8}}>
                      <div style={{fontSize:14,fontWeight:600,color:tk.text}}>{r.name}</div>
                      <button type="button" onClick={()=>handleDelete(r.id)} disabled={busyId===r.id} style={{background:'transparent',border:'none',cursor:'pointer',color:tk.muted,padding:2}}><Trash2 size={15}/></button>
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600,color:tk.sub}}><span style={{width:7,height:7,borderRadius:'50%',background:CAT_COLOR[r.category]}}/>{r.category}</span>
                      <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:11,color:tk.text}}><m.Icon size={12} color={tk.sub}/>{m.label}</span>
                      <span style={{background:cp.bg,color:cp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize'}}>{r.channel}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <Toggle active={r.status==='active'} onChange={()=>handleToggle(r.id)} tk={tk} disabled={busyId===r.id}/>
                        <span style={{fontSize:11,fontWeight:600,color:r.status==='active'?tk.good:tk.muted}}>{r.status==='active'?'Active':'Paused'}</span>
                      </div>
                      <span style={{fontSize:11,color:tk.muted}}>{toIST(r.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <div onClick={()=>setShowModal(false)} style={{position:'fixed',inset:0,background:tk.overlay,zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:16,padding:'22px 22px',width:'min(560px,100%)',maxHeight:'90vh',overflowY:'auto',boxSizing:'border-box'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <h2 style={{margin:0,fontSize:17,fontWeight:700,color:tk.text}}>Create Automation Rule</h2>
              <button type="button" onClick={()=>setShowModal(false)} style={{background:tk.btnSec,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:tk.sub}}><X size={15}/></button>
            </div>

            {/* Preset picker */}
            <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Choose a template</div>
            <div className="ato-presets" style={{marginBottom:16}}>
              {TRIGGERS.map(t => {
                const active = form.trigger_type===t.value;
                return (
                  <button key={t.value} type="button" onClick={()=>pickPreset(t.value)}
                    style={{textAlign:'left',display:'flex',flexDirection:'column',gap:6,padding:'11px 12px',borderRadius:10,cursor:'pointer',
                      background:active?tk.accentBg:tk.row, border:`1px solid ${active?tk.accent:tk.border}`}}>
                    <span style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:active?tk.accentTxt:tk.text}}>
                      <t.Icon size={14} color={CAT_COLOR[t.category]}/>{t.label}
                    </span>
                    <span style={{fontSize:10.5,color:tk.muted,lineHeight:1.35}}>{t.hint}</span>
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleCreate} style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:tk.sub,marginBottom:6}}>Rule name</label>
                <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. VIP launch invite — Diwali" style={inpStyle} required/>
              </div>
              <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                <div style={{flex:1,minWidth:160}}>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:tk.sub,marginBottom:6}}>Channel</label>
                  <select value={form.channel} onChange={e=>setForm(f=>({...f,channel:e.target.value}))} style={selStyle}>
                    {visibleChannels.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div style={{flex:1,minWidth:160}}>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:tk.sub,marginBottom:6}}>Template</label>
                  <input value={form.template} onChange={e=>setForm(f=>({...f,template:e.target.value}))} placeholder="Template name / ID" style={inpStyle}/>
                </div>
              </div>
              {curPreset.offsetLabel ? (
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:tk.sub,marginBottom:6}}>{curPreset.offsetLabel}</label>
                  <input type="number" value={form.offset_days} onChange={e=>setForm(f=>({...f,offset_days:e.target.value}))} style={inpStyle}/>
                </div>
              ) : null}
              <div style={{display:'flex',gap:10,paddingTop:4}}>
                <button type="button" onClick={()=>setShowModal(false)} style={{flex:1,padding:'11px',borderRadius:10,fontSize:13,fontWeight:600,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,cursor:'pointer'}}>Cancel</button>
                <button type="submit" disabled={submitting} style={{flex:1,padding:'11px',borderRadius:10,fontSize:13,fontWeight:600,background:tk.btnPri,color:tk.btnPriTxt,border:'none',cursor:'pointer',opacity:submitting?0.6:1}}>{submitting?'Creating…':'Create Rule'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}